import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  ProgressBar,
  Spinner,
  Table,
} from 'react-bootstrap';
import { ArrowClockwise, ArrowUpShort, CaretDownFill, CaretUpFill, Eye, Search } from 'react-bootstrap-icons';
import { ClientTags, ContractTag } from '@/components/clientFlags/ClientTags';
import { CONTRACT_DISPLAY, contractBadge, isCollectible, resolveContract, type Contract } from '@/config/contract';
import { useClientFlags } from '@/hooks/useClientFlags';
import { creditsService, DEFAULT_CREDIT_CONFIG, type CreditStanding } from '@/services/creditsService';
import { classifyAll } from '@/services/accountOrgService';
import { groupClientsByType } from '@/services/clientService';
import { ACCOUNT_ORG_DISPLAY } from '@/types';
import type { AccountOrgValue, Client, ClientConfig, CreditConfig } from '@/types';
import { contractText } from '@/utils/contractText';
import './FleetCreditsTable.css';

/**
 * Every client's credits in one table: its plan, the last six months of usage, its top-up
 * balance and who its billing admins are. Tick rows to change many clients at once.
 *
 * Each row joins three sources: the central client config (how the client pays, monthly
 * credits, whether credits show in the app), the client metadata (its organisation), and a
 * read of each client's own ledger (balance, usage, billing admins). The ledger read crosses
 * into every client account, eight at a time, so those columns fill in over a few seconds.
 *
 * The contract column shows the client's whole contract (`resolveContract`): the portal's
 * record, else the billing in its config. A client with neither shows "Not set". A client on
 * its own AWS account pays AWS directly and has no credits: every allocation and top-up
 * action skips it.
 *
 * Only a collectible client (`isCollectible`: on Credits, or a partner billed as Credits, and
 * not inactive) owes money for credits used past its monthly credits. The Owed and Prepaid
 * totals count those clients only. Every other client shows the same use as "past
 * allocation, not billed". The legacy figure says what active legacy clients would owe if
 * they were on credits.
 *
 * A client marked inactive is left out of the table until "Show inactive" is on. While it is
 * left out it cannot be ticked, so no bulk change reaches it.
 */

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type BulkAction =
  | 'set-allocation'
  | 'show-on'
  | 'show-off'
  | 'reset-topup'
  | 'add-topup'
  | 'contract-credits'
  | 'contract-legacy'
  | 'contract-own-account';

type ContractType = NonNullable<ClientConfig['contractType']>;

/** The contract each contract action saves. */
const CONTRACT_OF_ACTION: Partial<Record<BulkAction, ContractType>> = {
  'contract-credits': 'credits',
  'contract-legacy': 'legacy',
  'contract-own-account': 'own-account',
};

/** The heading of the confirm step, and the one sentence under it that says what will happen. */
const ACTION_LABEL: Record<BulkAction, string> = {
  'set-allocation': 'Set monthly credits',
  'show-on': "Show credits in the client's app",
  'show-off': "Hide credits in the client's app",
  'reset-topup': 'Reset top-up to zero',
  'add-topup': 'Add top-up',
  'contract-credits': 'Set contract to Credits',
  'contract-legacy': 'Set contract to Legacy',
  'contract-own-account': 'Set contract to Own account',
};

const ACTION_HELP: Record<BulkAction, string> = {
  'set-allocation':
    'Every month of the year is set to this many credits for each client below. Pricing stays as it is.',
  'show-on': "Each client's billing admins see credits in the app after that client's next deploy.",
  'show-off': "Credits leave each client's app after its next deploy. Usage is still metered.",
  'reset-topup':
    "Each client's top-up balance goes to zero. An adjustment is recorded in its ledger and nothing is deleted. To put a balance back, add a top-up.",
  'add-topup': "This many credits go on each client's top-up balance as soon as you apply.",
  'contract-credits':
    'Each client pays by Numa credits. This page uses it now; its app turns on the default-model settings at its next deploy. A partner or trial dates saved on a client are cleared.',
  'contract-legacy':
    'Each client is on an older contract. This page uses it now; its app hides the default-model settings at its next deploy. A partner or trial dates saved on a client are cleared.',
  'contract-own-account':
    'Each client runs in its own AWS account and pays AWS directly. From its next deploy its people see real AWS dollars, with no credits and no credit limit. Allocation and top-up actions skip it. A partner or trial dates saved on a client are cleared.',
};

/** Actions that move credits. A client on its own AWS account has none, so these skip it. */
const CREDIT_AMOUNT_ACTIONS: ReadonlySet<BulkAction> = new Set(['set-allocation', 'reset-topup', 'add-topup']);

const CONTRACT_WORD: Record<ContractType, string> = {
  credits: 'Credits',
  legacy: 'Legacy',
  'own-account': 'Own account',
};

interface PreviewRow {
  name: string;
  current: string;
  next: string;
  skip?: string;
}

interface BulkResult {
  name: string;
  outcome: 'ok' | 'skipped' | 'error';
  detail?: string;
}

interface Column {
  key: string;
  label: string;
  numeric?: boolean;
  title: string;
  /** The first column of a group gets a rule on its left. */
  groupStart?: boolean;
}

/** Deep-merge a partial credit config over the code defaults (mirrors NumaCredits.withDefaults).
 *  synergyRates (crawler-era, retired: Synergy bills at measured cost since TASK-380) is
 *  deliberately not merged or seeded; the schema still tolerates it on old records. */
function mergeCreditConfig(c?: CreditConfig): Omit<Required<CreditConfig>, 'synergyRates'> {
  return {
    ...DEFAULT_CREDIT_CONFIG,
    ...c,
    valueTiers: {
      chat: { ...DEFAULT_CREDIT_CONFIG.valueTiers.chat, ...c?.valueTiers?.chat },
      agent: { ...DEFAULT_CREDIT_CONFIG.valueTiers.agent, ...c?.valueTiers?.agent },
    },
    marginsByTier: { ...DEFAULT_CREDIT_CONFIG.marginsByTier, ...c?.marginsByTier },
    voiceRates: { ...DEFAULT_CREDIT_CONFIG.voiceRates, ...c?.voiceRates },
    monthlyAllocations:
      c?.monthlyAllocations && c.monthlyAllocations.length === 12
        ? c.monthlyAllocations
        : DEFAULT_CREDIT_CONFIG.monthlyAllocations,
  };
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export default function FleetCreditsTable({
  clients,
  loading = false,
  onOpenClient,
  onDataChanged,
}: {
  clients: Client[];
  /** The client list itself is still on its way. */
  loading?: boolean;
  onOpenClient: (name: string) => void;
  /** Called after a bulk write so the parent can refresh its client list (config cache cleared). */
  onDataChanged?: () => void;
}) {
  // Each client's saved record (its credits contract) and its tags, from the shared reader.
  const flags = useClientFlags();
  const [orgMap, setOrgMap] = useState<Map<string, AccountOrgValue | null>>(new Map());
  const [standings, setStandings] = useState<Map<string, CreditStanding | 'error'>>(new Map());
  const [sweeping, setSweeping] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [search, setSearch] = useState('');
  const [showDev, setShowDev] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Bulk-action modal state
  const [action, setAction] = useState<BulkAction | null>(null);
  const [actionValue, setActionValue] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<BulkResult[] | null>(null);

  // ── NZ billing calendar: current month + the trailing six ──
  const nzNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Pacific/Auckland' }));
  const monthCols = useMemo(() => {
    const cols: Array<{ key: string; label: string }> = [];
    for (let back = 5; back >= 0; back--) {
      const d = new Date(nzNow.getFullYear(), nzNow.getMonth() - back, 1);
      cols.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: MONTH_LABELS[d.getMonth()],
      });
    }
    return cols;
  }, [nzNow.getFullYear(), nzNow.getMonth()]);
  const curIdx = nzNow.getMonth();

  useEffect(() => {
    void classifyAll(clients).then(setOrgMap);
  }, [clients]);

  /** The client is inactive and the table is not showing inactive clients. */
  const hiddenAsInactive = (name: string) => !showInactive && flags.isInactive(name);

  // A client that leaves the table as inactive leaves the ticked set with it. A bulk change
  // acts on the ticked set, so a hidden client must never be in it.
  useEffect(() => {
    setSelected((current) => {
      const kept = [...current].filter((name) => !hiddenAsInactive(name));
      return kept.length === current.size ? current : new Set(kept);
    });
  }, [showInactive, flags]);

  // Auto-start the ledger sweep once clients are in: the usage/balance/admin columns are the
  // whole point of the table, so don't make the operator find the button. Refresh stays manual.
  const autoSwept = useRef(false);
  useEffect(() => {
    if (autoSwept.current || clients.length === 0) return;
    autoSwept.current = true;
    void sweep();
  }, [clients]);

  const configOf = (name: string): ClientConfig | undefined => clients.find((c) => c.name === name)?.config;
  /** The client's contract: the portal's record, else the billing in its config. Undefined = nobody has set it. */
  const contractOf = (name: string): Contract | undefined => resolveContract(flags.row(name), configOf(name));
  /** The contract in words, with the partner and how a partner is billed. */
  const contractWords = (name: string): string => contractText(flags.row(name), configOf(name)) ?? 'Not set';
  /** The client owes money for credits used past its monthly credits. */
  const collectible = (name: string): boolean =>
    isCollectible(contractOf(name), configOf(name)?.contractType, flags.isInactive(name));
  /** The client runs in its own AWS account and pays AWS itself, so it has no credits to change. */
  const paysAwsItself = (name: string): boolean =>
    contractOf(name) === 'own-account' || configOf(name)?.contractType === 'own-account';

  const allocOf = (c: Client): number => {
    const st = standings.get(c.name);
    if (st && st !== 'error' && st.ledgerAllocations) return st.ledgerAllocations[curIdx] ?? 0;
    return c.config.creditConfig?.monthlyAllocations?.[curIdx] ?? DEFAULT_CREDIT_CONFIG.monthlyAllocations[0] ?? 0;
  };

  // ── on-demand cross-account sweep (8 workers, same pattern as the per-client standing read) ──
  const sweep = async (names?: string[]) => {
    const targets = names ? clients.filter((c) => names.includes(c.name)) : clients;
    setSweeping(true);
    setError(null);
    const queue = [...targets];
    setProgress({ done: 0, total: targets.length });
    let done = 0;
    const next = new Map(standings);
    const worker = async () => {
      for (let c = queue.shift(); c; c = queue.shift()) {
        try {
          next.set(c.name, await creditsService.getStanding(c.name));
        } catch {
          next.set(c.name, 'error');
        }
        done += 1;
        setProgress({ done, total: targets.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(8, targets.length) }, worker));
    setStandings(new Map(next));
    setSweeping(false);
  };

  // ── sorting ──
  const [sortKey, setSortKey] = useState('client');
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const clickSort = (key: string) => {
    if (key === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDir(1);
    }
  };
  /** Per-column sort value; null = "no data", always sorted last regardless of direction. */
  const sortValue = (c: Client): string | number | null => {
    const st = standings.get(c.name);
    const standing = st && st !== 'error' ? st : null;
    if (sortKey.startsWith('month:')) return standing?.months[sortKey.slice(6)]?.used ?? null;
    switch (sortKey) {
      case 'org':
        return orgMap.get(c.name) ?? null;
      case 'credits':
        return contractOf(c.name) ? contractWords(c.name) : null;
      case 'visible':
        return c.config.showCredits ? 1 : 0;
      case 'alloc':
        return allocOf(c);
      case 'balance':
        return standing ? standing.availableBalance : null;
      case 'admins':
        return standing ? standing.billingAdmins.length : null;
      default:
        return c.name;
    }
  };

  // ── row set ──
  const { devClients, productionClients } = useMemo(() => groupClientsByType(clients), [clients]);
  const visible = useMemo(() => {
    const base = showDev ? [...productionClients, ...devClients] : productionClients;
    const q = search.trim().toLowerCase();
    return base
      .filter((c) => !hiddenAsInactive(c.name))
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .filter((c) => !attentionOnly || !contractOf(c.name))
      .sort((a, b) => {
        const va = sortValue(a);
        const vb = sortValue(b);
        if (va == null && vb == null) return a.name.localeCompare(b.name);
        if (va == null) return 1; // no-data rows last, both directions
        if (vb == null) return -1;
        const cmp =
          typeof va === 'string' || typeof vb === 'string'
            ? String(va).localeCompare(String(vb))
            : (va as number) - (vb as number);
        return (cmp || a.name.localeCompare(b.name)) * sortDir;
      });
  }, [
    productionClients,
    devClients,
    showDev,
    showInactive,
    flags,
    search,
    attentionOnly,
    standings,
    orgMap,
    sortKey,
    sortDir,
  ]);
  // Inactive clients the table is leaving out, among the stacks it would otherwise list.
  const inactiveLeftOut = (showDev ? [...productionClients, ...devClients] : productionClients).filter((c) =>
    hiddenAsInactive(c.name)
  ).length;

  const allVisibleSelected = visible.length > 0 && visible.every((c) => selected.has(c.name));
  const toggleAll = () => setSelected(allVisibleSelected ? new Set() : new Set(visible.map((c) => c.name)));
  const toggleOne = (name: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // ── totals (from whatever the sweep has read) ──
  const agg = useMemo(() => {
    // Owed and prepaid: the clients billed for credits only. A legacy, own-account, trial or
    // internal stack also meters credits and can end a month below zero, and nobody pays that.
    let owed = 0;
    let prepaid = 0;
    let billedRead = 0;
    // Used this month: the active clients, and the inactive ones too while they are shown.
    let usedThisMonth = 0;
    let usedRead = 0;
    let failed = 0;
    // What active legacy clients used past their monthly credits in the last full month. This
    // month is only days old for much of it, so it would read near 0.
    let legacyPast = 0;
    let legacyUsd = 0;
    let legacyOver = 0;
    let legacyRead = 0;
    let read = 0;
    const curKey = monthCols[monthCols.length - 1]?.key ?? '';
    const lastKey = monthCols[monthCols.length - 2]?.key ?? '';
    for (const c of clients) {
      const st = standings.get(c.name);
      if (!st) continue;
      const inactive = flags.isInactive(c.name);
      const counted = !inactive || showInactive;
      if (st === 'error') {
        if (counted) failed += 1;
        continue;
      }
      read += 1;
      if (collectible(c.name)) {
        billedRead += 1;
        if (st.availableBalance < 0) owed += -st.availableBalance;
        else prepaid += st.availableBalance;
      }
      const month = st.months[curKey];
      if (counted) {
        usedRead += 1;
        usedThisMonth += month?.used ?? 0;
      }
      if (!inactive && contractOf(c.name) === 'legacy') {
        legacyRead += 1;
        const last = st.months[lastKey];
        const past = last ? Math.max(0, last.used - Math.max(0, last.allocation)) : 0;
        if (past > 0) {
          legacyOver += 1;
          legacyPast += past;
          legacyUsd += past * (c.config.creditConfig?.creditUsd ?? DEFAULT_CREDIT_CONFIG.creditUsd);
        }
      }
    }
    return {
      owed,
      prepaid,
      billedRead,
      usedThisMonth,
      usedRead,
      failed,
      legacyPast,
      legacyUsd,
      legacyOver,
      legacyRead,
      read,
    };
  }, [clients, standings, monthCols, flags, showInactive]);

  // ── bulk action plumbing ──
  const numValue = Number(actionValue);
  const valueRequired = action === 'set-allocation' || action === 'add-topup';
  const valueValid = !valueRequired || (Number.isFinite(numValue) && numValue > 0);

  const preview: PreviewRow[] = useMemo(() => {
    if (!action) return [];
    return [...selected]
      .sort()
      .map((name) => {
        const client = clients.find((c) => c.name === name);
        if (!client) return { name, current: '', next: '', skip: 'Client not found' };
        // The ticked set never holds a hidden client. This is the second lock on that door.
        if (hiddenAsInactive(name)) return { name, current: '', next: '', skip: 'Skipped: inactive and not shown' };
        if (CREDIT_AMOUNT_ACTIONS.has(action) && paysAwsItself(name))
          return { name, current: '', next: '', skip: 'Skipped: pays AWS directly, so it has no credits' };
        const st = standings.get(name);
        const standing = st && st !== 'error' ? st : null;
        switch (action) {
          case 'set-allocation': {
            const cur = allocOf(client);
            if (cur === numValue) return { name, current: cur.toLocaleString(), next: '', skip: 'Already set' };
            return {
              name,
              current: `${cur.toLocaleString()} a month`,
              next: `${numValue.toLocaleString()} a month`,
            };
          }
          case 'show-on':
          case 'show-off': {
            const cur = !!client.config.showCredits;
            const next = action === 'show-on';
            if (cur === next) return { name, current: cur ? 'Shown' : 'Hidden', next: '', skip: 'Already set' };
            return { name, current: cur ? 'Shown' : 'Hidden', next: next ? 'Shown' : 'Hidden' };
          }
          case 'reset-topup': {
            if (!standing) return { name, current: '', next: '', skip: 'Balance not read. Refresh balances first.' };
            if (Math.abs(standing.availableBalance) < 0.005)
              return { name, current: '0', next: '', skip: 'Already zero' };
            return { name, current: standing.availableBalance.toLocaleString(), next: '0' };
          }
          case 'add-topup': {
            const cur = standing ? standing.availableBalance.toLocaleString() : 'Not read';
            return { name, current: cur, next: `+${numValue.toLocaleString()}` };
          }
          case 'contract-credits':
          case 'contract-legacy':
          case 'contract-own-account': {
            // A trial or a partner on Credits billing is not "already" on Credits: the change ends it.
            const next = CONTRACT_OF_ACTION[action]!;
            const current = contractWords(name);
            if (contractOf(name) === next) return { name, current, next: '', skip: 'Already set' };
            return { name, current, next: CONTRACT_WORD[next] };
          }
        }
      })
      .filter((r): r is PreviewRow => !!r);
  }, [action, selected, clients, standings, numValue, showInactive, flags]);

  const actionable = preview.filter((r) => !r.skip);

  const execute = async () => {
    if (!action) return;
    setRunning(true);
    setResults(null);
    const queue = [...actionable];
    setRunProgress({ done: 0, total: queue.length });
    const out: BulkResult[] = preview
      .filter((r) => r.skip)
      .map((r) => ({ name: r.name, outcome: 'skipped' as const, detail: r.skip }));
    let done = 0;
    const worker = async () => {
      for (let row = queue.shift(); row; row = queue.shift()) {
        const client = clients.find((c) => c.name === row.name)!;
        try {
          switch (action) {
            case 'set-allocation': {
              const merged = mergeCreditConfig(client.config.creditConfig);
              merged.monthlyAllocations = Array.from({ length: 12 }, () => numValue);
              await creditsService.saveConfig(client.name, merged);
              break;
            }
            case 'show-on':
              await creditsService.setVisibility(client.name, true);
              break;
            case 'show-off':
              await creditsService.setVisibility(client.name, false);
              break;
            case 'reset-topup': {
              const st = standings.get(client.name);
              if (!st || st === 'error') throw new Error('The balance was not read.');
              await creditsService.adjustBalance(
                client.name,
                -st.availableBalance,
                'bulk reset to zero (portal fleet table)'
              );
              break;
            }
            case 'add-topup':
              await creditsService.topUp(client.name, numValue);
              break;
            case 'contract-credits':
            case 'contract-legacy':
            case 'contract-own-account':
              // The contract and its billing are written. The rest of the client's config stays as it is.
              await creditsService.setContract(client.name, CONTRACT_OF_ACTION[action]!);
              break;
          }
          out.push({ name: row.name, outcome: 'ok' });
        } catch (e) {
          out.push({ name: row.name, outcome: 'error', detail: e instanceof Error ? e.message : String(e) });
        }
        done += 1;
        setRunProgress({ done, total: actionable.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
    out.sort((a, b) => a.name.localeCompare(b.name));
    setResults(out);
    setRunning(false);
    // Refresh what the action touched: the parent reloads the client configs, and the ledger
    // standings are read again here.
    if (action === 'reset-topup' || action === 'add-topup' || action === 'set-allocation') {
      void sweep(actionable.map((r) => r.name));
    }
    onDataChanged?.();
  };

  const openAction = (a: BulkAction) => {
    setAction(a);
    setActionValue('');
    setConfirmed(false);
    setResults(null);
  };

  const closeModal = () => {
    setAction(null);
    setActionValue('');
    setConfirmed(false);
    setResults(null);
    if (results?.some((r) => r.outcome === 'ok')) setSelected(new Set());
  };

  // Credits is the usual answer, so it stays plain; the others get a pill with the whole contract.
  const contractCell = (name: string) => {
    const contract = contractOf(name);
    if (contract === 'credits') return <span>Credits</span>;
    if (!contract)
      return (
        <Badge bg="warning" title="No contract is saved for this client. Tick the row, then use Contract.">
          Not set
        </Badge>
      );
    const badge = contractBadge(flags.row(name), configOf(name));
    return (
      <ContractTag
        label={contractWords(name)}
        variant={badge?.variant ?? CONTRACT_DISPLAY[contract].variant}
        hint={CONTRACT_DISPLAY[contract].hint}
      />
    );
  };

  const orgCell = (name: string) => {
    const v = orgMap.get(name);
    return v ? ACCOUNT_ORG_DISPLAY[v].label : '';
  };

  const thisMonth = MONTH_LABELS[curIdx];
  const lastMonth = MONTH_LABELS[(curIdx + 11) % 12];
  const columns: Column[] = [
    { key: 'client', label: 'Client', title: 'Sort by name' },
    { key: 'org', label: 'Organisation', title: "The AWS organisation that holds the client's account" },
    {
      key: 'credits',
      label: 'Contract',
      title:
        'The contract: Credits, Own account, Trial, Partner, Internal or Legacy. A partner shows how it is billed.',
      groupStart: true,
    },
    { key: 'visible', label: 'In its app', title: 'Whether the client sees credits in its app' },
    {
      key: 'alloc',
      label: 'Monthly credits',
      numeric: true,
      title: `The credits the client starts ${thisMonth} with. Read from its ledger once that is read, else from its saved plan, else the default.`,
    },
    ...monthCols.map((m, i) => ({
      key: `month:${m.key}`,
      label: m.label,
      numeric: true,
      title: `Credits used in ${m.key}`,
      groupStart: i === 0,
    })),
    {
      key: 'balance',
      label: 'Top-up balance',
      numeric: true,
      title:
        'Credits on the top-up balance now. Below zero is owed only by a client billed for credits. For any other client it is use past its monthly credits, and nobody is billed for it.',
      groupStart: true,
    },
    { key: 'admins', label: 'Billing admins', title: 'Who has permanent billing access in the client' },
  ];
  const columnCount = columns.length + 1;

  const emptyText = attentionOnly
    ? 'Every client here has a contract type set.'
    : search.trim()
      ? 'No client matches that search.'
      : 'No clients to show.';
  const inactiveNote =
    inactiveLeftOut > 0 ? `${plural(inactiveLeftOut, 'inactive client is', 'inactive clients are')} not shown.` : '';

  return (
    <section className="fleet-credits">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Totals across the ledgers read so far ── */}
      {agg.read > 0 && (
        <div className="fleet-credits__totals">
          <div className="fleet-credits__total">
            <div className="fleet-credits__total-label">Owed, credits</div>
            <div className={`fleet-credits__total-value numeric${agg.owed > 0 ? ' is-owed' : ''}`}>
              {Math.round(agg.owed).toLocaleString()}
            </div>
            <div className="fleet-credits__total-sub">
              Balances below zero, added up, for the {plural(agg.billedRead, 'client', 'clients')} billed for credits
            </div>
          </div>
          <div className="fleet-credits__total">
            <div className="fleet-credits__total-label">Prepaid, credits</div>
            <div className="fleet-credits__total-value numeric">{Math.round(agg.prepaid).toLocaleString()}</div>
            <div className="fleet-credits__total-sub">Balances above zero, added up, for the same clients</div>
          </div>
          <div className="fleet-credits__total">
            <div className="fleet-credits__total-label">Used in {thisMonth}, credits</div>
            <div className="fleet-credits__total-value numeric">{Math.round(agg.usedThisMonth).toLocaleString()}</div>
            <div className="fleet-credits__total-sub">
              {showInactive
                ? `Across ${plural(agg.usedRead, 'client', 'clients')}, inactive ones included`
                : `Across ${plural(agg.usedRead, 'active client', 'active clients')}`}
              , dev and demo stacks included
              {agg.failed > 0 && `. ${plural(agg.failed, 'client has', 'clients have')} no readable ledger.`}
            </div>
          </div>
          <div className="fleet-credits__total">
            <div className="fleet-credits__total-label">Legacy, past monthly credits in {lastMonth}</div>
            <div className="fleet-credits__total-value numeric">{Math.round(agg.legacyPast).toLocaleString()}</div>
            <div className="fleet-credits__total-sub">
              If legacy clients were on credits: about US$
              {Math.round(agg.legacyUsd).toLocaleString()} in {lastMonth}, from {agg.legacyOver.toLocaleString()} of{' '}
              {plural(agg.legacyRead, 'active legacy client', 'active legacy clients')}
            </div>
          </div>
        </div>
      )}

      {/* ── Find and filter ── */}
      <div className="fleet-credits__toolbar">
        <InputGroup className="fleet-credits__search">
          <InputGroup.Text>
            <Search />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search clients"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search clients"
          />
        </InputGroup>
        <Form.Check
          type="switch"
          id="fleet-show-dev"
          label="Include dev and demo"
          checked={showDev}
          onChange={(e) => setShowDev(e.target.checked)}
        />
        <Form.Check
          type="switch"
          id="fleet-show-inactive"
          label="Show inactive"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        <Form.Check
          type="switch"
          id="fleet-attention"
          label="Only contracts not set"
          checked={attentionOnly}
          onChange={(e) => setAttentionOnly(e.target.checked)}
        />
        <span className="fleet-credits__toolbar-end">
          {!loading && (
            <span className="fleet-credits__count" title={inactiveNote || undefined}>
              {plural(visible.length, 'client', 'clients')}
              {inactiveLeftOut > 0 && `, ${inactiveLeftOut.toLocaleString()} inactive not shown`}
            </span>
          )}
          <Button size="sm" variant="outline-secondary" disabled={sweeping || loading} onClick={() => void sweep()}>
            {sweeping ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Reading {progress.done} of {progress.total}
              </>
            ) : (
              <>
                <ArrowClockwise className="me-1" />
                Refresh balances
              </>
            )}
          </Button>
        </span>
      </div>

      <Card className="fleet-credits__card">
        {sweeping && (
          <ProgressBar
            className="fleet-credits__progress"
            now={progress.total ? (100 * progress.done) / progress.total : 0}
            aria-label={`Reading ${progress.done} of ${progress.total} ledgers`}
          />
        )}

        {/* ── The actions for the ticked rows. They show only when a row is ticked. ── */}
        {selected.size > 0 && (
          <div className="fleet-credits__selection" role="toolbar" aria-label="Change the selected clients">
            <span className="fleet-credits__selection-count">{selected.size.toLocaleString()} selected</span>
            <Button size="sm" variant="link" className="fleet-credits__clear" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <span className="fleet-credits__rule" aria-hidden="true" />
            <Button size="sm" variant="outline-secondary" onClick={() => openAction('set-allocation')}>
              Set monthly credits
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => openAction('add-topup')}>
              Add top-up
            </Button>
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="outline-secondary">
                Credits in the app
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => openAction('show-on')}>Show credits</Dropdown.Item>
                <Dropdown.Item onClick={() => openAction('show-off')}>Hide credits</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="outline-secondary">
                Contract
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => openAction('contract-credits')}>Credits</Dropdown.Item>
                <Dropdown.Item onClick={() => openAction('contract-legacy')}>Legacy</Dropdown.Item>
                <Dropdown.Item onClick={() => openAction('contract-own-account')}>Own account</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Button
              size="sm"
              variant="outline-danger"
              className="fleet-credits__danger"
              onClick={() => openAction('reset-topup')}
            >
              Reset top-up to zero
            </Button>
          </div>
        )}

        <div className="fleet-credits__scroll">
          <Table hover className="mb-0 fleet-credits__table">
            <thead>
              <tr className="fleet-credits__groups">
                <th colSpan={3} />
                <th colSpan={3} scope="colgroup" className="fleet-credits__group-start">
                  Plan
                </th>
                <th colSpan={monthCols.length} scope="colgroup" className="fleet-credits__group-start">
                  Credits used each month
                </th>
                <th colSpan={2} scope="colgroup" className="fleet-credits__group-start">
                  Now
                </th>
              </tr>
              <tr>
                <th className="fleet-credits__check">
                  <Form.Check
                    type="checkbox"
                    aria-label="Select every client shown"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                  />
                </th>
                {columns.map((h) => (
                  <th
                    key={h.key}
                    className={`${h.numeric ? 'text-end ' : ''}${h.groupStart ? 'fleet-credits__group-start' : ''}`}
                    aria-sort={sortKey === h.key ? (sortDir === 1 ? 'ascending' : 'descending') : undefined}
                  >
                    <button
                      type="button"
                      className="fleet-credits__sort"
                      title={h.title}
                      onClick={() => clickSort(h.key)}
                    >
                      {h.label}
                      {sortKey === h.key && (sortDir === 1 ? <CaretUpFill /> : <CaretDownFill />)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columnCount} className="fleet-credits__empty">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading clients
                  </td>
                </tr>
              )}
              {!loading &&
                visible.map((c) => {
                  const st = standings.get(c.name);
                  const standing = st && st !== 'error' ? st : null;
                  const bal = standing?.availableBalance;
                  // Below zero is money owed only by a client billed for credits.
                  // Shown to the whole credit, so a balance a fraction below zero reads as 0, not "-0".
                  const below = bal != null && Math.round(bal) < 0;
                  const owes = below && collectible(c.name);
                  const admins = standing ? standing.billingAdmins.map((a) => a.email ?? a.sub).join(', ') : '';
                  return (
                    <tr key={c.name} className={selected.has(c.name) ? 'fleet-credits__row--selected' : undefined}>
                      <td className="fleet-credits__check">
                        <Form.Check
                          type="checkbox"
                          aria-label={`Select ${c.name}`}
                          checked={selected.has(c.name)}
                          onChange={() => toggleOne(c.name)}
                        />
                      </td>
                      <td>
                        <button type="button" className="fleet-credits__client" onClick={() => onOpenClient(c.name)}>
                          {c.name}
                        </button>
                        <ClientTags clientName={c.name} flags={flags} hide={['deploy-block', 'contract']} />
                        {st === 'error' && (
                          <span
                            className="fleet-credits__no-ledger"
                            title="This client's ledger could not be read. It may not exist yet, or access was refused."
                          >
                            No ledger
                          </span>
                        )}
                      </td>
                      <td className="fleet-credits__org">{orgCell(c.name)}</td>
                      <td className="fleet-credits__group-start">{contractCell(c.name)}</td>
                      <td>
                        {c.config.showCredits ? (
                          <span className="fleet-credits__shown">
                            <Eye aria-hidden="true" /> Shown
                          </span>
                        ) : (
                          <span className="fleet-credits__hidden">Hidden</span>
                        )}
                      </td>
                      <td className="text-end numeric">{allocOf(c).toLocaleString()}</td>
                      {monthCols.map((m, i) => {
                        const used = standing?.months[m.key]?.used;
                        const alloc = standing?.months[m.key]?.allocation;
                        const over = used != null && alloc != null && used > alloc;
                        return (
                          <td
                            key={m.key}
                            className={`text-end numeric${i === 0 ? ' fleet-credits__group-start' : ''}${over ? ' fleet-credits__over' : ''}`}
                            title={over ? `Over that month's ${Math.round(alloc).toLocaleString()} credits` : undefined}
                          >
                            {over && <ArrowUpShort aria-label="Over the month's credits" />}
                            {used != null ? Math.round(used).toLocaleString() : ''}
                          </td>
                        );
                      })}
                      <td
                        className={`text-end numeric fleet-credits__group-start fleet-credits__balance${owes ? ' is-owed' : below ? ' is-unbilled' : ''}`}
                        title={
                          owes
                            ? `Owed: ${Math.round(-bal).toLocaleString()} credits`
                            : below
                              ? `Past allocation, not billed: ${Math.round(-bal).toLocaleString()} credits`
                              : undefined
                        }
                      >
                        {bal != null ? (Math.round(bal) || 0).toLocaleString() : ''}
                        {below && !owes && <div className="fleet-credits__unbilled">Past allocation, not billed</div>}
                      </td>
                      <td className="fleet-credits__admins" title={admins || undefined}>
                        {standing ? admins || <span className="fleet-credits__none">None yet</span> : ''}
                      </td>
                    </tr>
                  );
                })}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="fleet-credits__empty">
                    {emptyText}
                    {inactiveNote && ` ${inactiveNote}`}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      <p className="fleet-credits__foot">
        A month in red with an arrow went over that month&apos;s credits. Usage, balances and billing admins are read
        from each client&apos;s own ledger, so those columns fill in a few seconds after the page opens. Only a client
        on Credits, or a partner billed as Credits, owes for use past its monthly credits. Any other client shows that
        use as past allocation, and nobody is billed for it. Changes to credits skip a client on its own AWS account. An
        inactive client is left out of the table, and of every bulk change, until Show inactive is on.
      </p>

      {/* ── A bulk change: read what will happen, confirm, then see how each client went ── */}
      <Modal show={!!action} onHide={running ? undefined : closeModal} size="lg" centered>
        <Modal.Header closeButton={!running}>
          <Modal.Title>{action ? ACTION_LABEL[action] : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {action && !results && (
            <p className={`fleet-credits__help${action === 'reset-topup' ? ' fleet-credits__help--danger' : ''}`}>
              {ACTION_HELP[action]}
            </p>
          )}
          {valueRequired && !results && (
            <InputGroup className="fleet-credits__value">
              <InputGroup.Text>{action === 'set-allocation' ? 'Credits a month' : 'Credits'}</InputGroup.Text>
              <Form.Control
                type="number"
                step="100"
                min="0"
                autoFocus
                value={actionValue}
                onChange={(e) => setActionValue(e.target.value)}
                placeholder="1000"
                aria-label={action === 'set-allocation' ? 'Credits a month' : 'Credits'}
              />
            </InputGroup>
          )}
          {!results ? (
            <>
              <div className="fleet-credits__preview">
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th className="text-end">Now</th>
                      <th className="text-end">After</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r) => (
                      <tr key={r.name} className={r.skip ? 'fleet-credits__skipped' : undefined}>
                        <td>{r.name}</td>
                        <td className="text-end numeric">{r.current}</td>
                        <td className="text-end numeric fleet-credits__after">{r.next}</td>
                        <td>{r.skip ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {running ? (
                <div className="fleet-credits__running">
                  <ProgressBar now={runProgress.total ? (100 * runProgress.done) / runProgress.total : 0} />
                  <span className="numeric">
                    {runProgress.done} of {runProgress.total} done
                  </span>
                </div>
              ) : (
                <Form.Check
                  className="mt-3"
                  type="checkbox"
                  id="bulk-confirm"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  label={`I have read the ${plural(actionable.length, 'change', 'changes')} above`}
                />
              )}
            </>
          ) : (
            <>
              <p className="fleet-credits__outcome">
                {results.filter((r) => r.outcome === 'ok').length} applied,{' '}
                {results.filter((r) => r.outcome === 'skipped').length} skipped,{' '}
                <span className={results.some((r) => r.outcome === 'error') ? 'fleet-credits__failed' : undefined}>
                  {results.filter((r) => r.outcome === 'error').length} failed
                </span>
                .
              </p>
              <div className="fleet-credits__preview">
                <Table size="sm" className="mb-0">
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.name}>
                        <td>{r.name}</td>
                        <td>
                          {r.outcome === 'ok' ? (
                            <Badge bg="success">Done</Badge>
                          ) : r.outcome === 'skipped' ? (
                            <Badge bg="secondary">Skipped</Badge>
                          ) : (
                            <Badge bg="danger">Failed</Badge>
                          )}
                        </td>
                        <td className="fleet-credits__detail">{r.detail ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!results ? (
            <>
              <Button variant="outline-secondary" disabled={running} onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant={action === 'reset-topup' ? 'danger' : 'primary'}
                disabled={running || !confirmed || !valueValid || actionable.length === 0}
                onClick={() => void execute()}
              >
                {running ? 'Applying' : `Apply to ${plural(actionable.length, 'client', 'clients')}`}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={closeModal}>
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </section>
  );
}
